import React from 'react';
import { motion } from 'framer-motion';
import Avatar from '../UI/Avatar';
import LevelBadge from '../UI/LevelBadge';
import BadgeShield from '../UI/BadgeShield';

/**
 * Composant affichant les deux joueurs en mode Duel avec le VS au centre.
 * player1/player2 peuvent inclure un champ `badges: []` pour afficher les écussons.
 */
function PlayerVS({ player1, player2, score1 = 0, score2 = 0, maxScore = 5 }) {
  return (
    <div className="flex items-center justify-between gap-2 bg-black/30 rounded-2xl p-4 border border-white/10">
      {/* Joueur 1 (utilisateur) */}
      <PlayerCard
        user={player1}
        score={score1}
        maxScore={maxScore}
        isCurrentUser={true}
        align="left"
      />

      {/* VS au centre */}
      <motion.div
        className="flex-shrink-0 flex flex-col items-center"
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        <span
          className="text-2xl font-black tracking-widest"
          style={{
            fontFamily: "'Rajdhani', sans-serif",
            color: '#f0b429',
            textShadow: '0 0 20px rgba(240,180,41,0.6)',
          }}
        >
          VS
        </span>
        <div className="text-white/40 text-xs mt-1 font-medium">
          {score1} - {score2}
        </div>
      </motion.div>

      {/* Joueur 2 (adversaire) */}
      <PlayerCard
        user={player2}
        score={score2}
        maxScore={maxScore}
        isCurrentUser={false}
        align="right"
      />
    </div>
  );
}

function PlayerCard({ user, score, maxScore, isCurrentUser, align }) {
  const isLeft  = align === 'left';
  const badges  = user?.badges || [];

  return (
    <div className={`flex flex-col gap-1.5 flex-1 ${isLeft ? 'items-start' : 'items-end'}`}>
      <div className={`flex items-center gap-2 ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}>
        <Avatar
          user={user}
          size="lg"
          showBorder={isCurrentUser}
        />
        <div className={`flex flex-col ${isLeft ? 'items-start' : 'items-end'}`}>
          <span
            className="text-white font-bold text-sm truncate max-w-[80px]"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            {user?.pseudo || (isCurrentUser ? 'Vous' : 'Adversaire')}
          </span>
          <LevelBadge level={user?.niveau || user?.level || 1} size="xs" />

          {/* Écussons — max 4 affichés */}
          {badges.length > 0 && (
            <div className={`flex gap-1 mt-1 flex-wrap ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}>
              {badges.slice(0, 4).map((badge) => (
                <BadgeShield key={badge.id || badge.nom} badge={badge} size={20} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Score dots */}
      <div className={`flex gap-1 ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}>
        {Array.from({ length: maxScore }).map((_, i) => (
          <div
            key={i}
            className={`w-3 h-3 rounded-full border transition-all duration-300 ${
              i < score
                ? isCurrentUser
                  ? 'bg-qfl-gold border-qfl-gold shadow-[0_0_6px_rgba(240,180,41,0.6)]'
                  : 'bg-qfl-red border-qfl-red shadow-[0_0_6px_rgba(239,68,68,0.6)]'
                : 'bg-transparent border-white/30'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default PlayerVS;
